/**
 * Resource limits. Every value is configurable through the environment so that
 * an operator can tighten them without touching code. Video work is expensive;
 * anything outside these bounds is rejected before it reaches a worker.
 */
/**
 * `core` is imported by the browser bundle as well as the server, so it cannot
 * assume `process` exists. In the browser every limit falls back to its default,
 * which is correct: the values here are advisory in the editor and enforced for
 * real on the server.
 */
function readEnv(name: string): string | undefined {
  if (typeof process === "undefined" || !process.env) return undefined;
  return process.env[name];
}

function intFromEnv(name: string, fallback: number): number {
  const raw = readEnv(name);
  if (raw === undefined || raw.trim() === "") return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const limits = {
  get maxUploadBytes() {
    return intFromEnv("MAX_UPLOAD_SIZE_MB", 500) * 1024 * 1024;
  },
  /**
   * A card still is not a recording. The recording limit is sized for hundreds
   * of megabytes of screen capture; letting a logo through the same gate would
   * accept a 500MB PNG nobody meant to send.
   */
  get maxImageUploadBytes() {
    return intFromEnv("MAX_IMAGE_UPLOAD_SIZE_MB", 25) * 1024 * 1024;
  },
  get maxVideoDurationSeconds() {
    return intFromEnv("MAX_VIDEO_DURATION_SECONDS", 3600);
  },
  get maxRenderDurationSeconds() {
    return intFromEnv("MAX_RENDER_DURATION_SECONDS", 3600);
  },
  get workerConcurrency() {
    return intFromEnv("WORKER_CONCURRENCY", 1);
  },
  get maxScenes() {
    return intFromEnv("MAX_SCENES", 500);
  },
  get maxTranscriptLength() {
    return intFromEnv("MAX_TRANSCRIPT_LENGTH", 100_000);
  },
  get maxActions() {
    return intFromEnv("MAX_ACTIONS", 500);
  },
  get maxCaptions() {
    return intFromEnv("MAX_CAPTIONS", 20_000);
  },
} as const;

/** MIME types we are willing to accept as a BODY recording. */
export const allowedVideoMimeTypes = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/x-matroska",
  "video/x-msvideo",
] as const;

export const allowedImageMimeTypes = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;

/** A font file is small; anything past this is not a font someone means to use. */
export const maxFontUploadBytes = 6 * 1024 * 1024;

export const allowedAudioMimeTypes = ["audio/mpeg", "audio/mp4", "audio/wav", "audio/webm", "audio/ogg"] as const;
